import { VideoQuality } from './types';
import DeviceManager from '../../../util/deviceManager';
import TreedisPublicDAO from '../../../../api/TreedisPublicDAO';

export default class VimeoVideo {
  public readonly id: string;

  public data: any;

  public selectedQuality: VideoQuality;

  constructor(videoId: string) {
    this.id = videoId;
    this.selectedQuality = VideoQuality.X720;
  }

  static parseVideoId(url: string) {
    return parseInt(url.toString().match(/([0-9]+)/)[1], 10);
  }

  /**
   * Load a specific video by providing a Vimeo video ID
   */
  async loadFromVideoId(clientId: number) {
    if (!this.id) {
      throw new Error('[Vimeo] No video ID was specified');
    }
    if (!this.data) {
      this.data = await TreedisPublicDAO.getVimeoVideo(this.id, clientId);
    }
  }

  /**
   * Get the current Vimeo video file URL
   * @returns {string}
   */
  getFileURL() {
    if (this.isAdaptivePlayback()) {
      return this.getAdaptiveURL();
    }
    return this.getProgressiveFileURL(this.selectedQuality);
  }

  // eslint-disable-next-line consistent-return
  getAdaptiveURL() {
    if (this.isDashPlayback()) {
      if (this.data) {
        return this.data.play.dash.link;
      }
    } else if (this.data) {
      return this.data.play.hls.link;
    }
  }

  // eslint-disable-next-line consistent-return
  getProgressiveFileURL(quality: VideoQuality) {
    if (!this.isLive() && this.data) {
      if (this.data.play.progressive) {
        this.data.play.progressive.sort((a: any, b: any) => (a.height < b.height ? 1 : -1));

        const preferredQualities = [];
        // eslint-disable-next-line no-plusplus
        for (let i = 0; i < this.data.play.progressive.length; i++) {
          if (quality > this.data.play.progressive[i].height) {
            preferredQualities.push(this.data.play.progressive[i]);
          } else if (quality === this.data.play.progressive[i].height) {
            return this.data.play.progressive[i].link;
          }
        }

        if (preferredQualities.length === 0) {
          const file = this.data.play.progressive[this.data.play.progressive.length - 1];
          return file.link;
        }
        return preferredQualities[0].link;
      }
    }
  }

  /**
   * Query wheter the current video is a livestream
   * @returns {bool}
   */
  isLive() {
    if (this.data) {
      return this.data.live && this.data.live.status === 'streaming';
    }
    return false;
  }

  /**
   * Query wheter the current video is playing back an adaptive stream
   * @returns {bool}
   */
  isAdaptivePlayback() {
    return this.selectedQuality === VideoQuality.Auto || this.selectedQuality === VideoQuality.Adaptive;
  }

  /**
   * Query wheter the current video is playing back an adaptive DASH stream
   * @returns {bool}
   */
  isDashPlayback() {
    return this.isAdaptivePlayback() && !DeviceManager.isIOS();
  }
}
